import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./ApprovalStatusRenderer.module.css";
import { Button, Overlay, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { createPortal } from "react-dom";
import CygnusGrid from "../../../../../pages/reusable_components/CygnusGrid/CygnusGrid";
import { Bars } from "react-loader-spinner";
import { fetchApprovalPageData } from "./ApprovalStatusRenderer.service";import {
  TimeRenderer,
} from "../../../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import MessageCellRenderer from "./components/MessageCellRenderer/MessageCellRenderer";
import { ELApproverData, groupCOIApprovers } from "./ApprovalStatusRenderer.utils";

/**
 * ApprovalStatusRenderer Component
 */
const ApprovalStatusRenderer: React.FC = (props: any) => {
  const [showPopup, setShowPopup] = useState(false);
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<any | null>(null);
    const [approversData, setApproversData] = useState<ELApproverData[]>([]);
    const [approvalStatusRefreshGrid, setApprovalStatusRefreshGrid] = useState(false);
    const popupRef = useRef<HTMLDivElement | null>(null);

    const loadApprovalPageData = useCallback(async () => {
      try {
        setApprovalStatusRefreshGrid(true);
        const details = await fetchApprovalPageData(props.data.ProcessedTemplateId);
        setApproversData(details);
      } catch (error) {
        console.error(error);
      } finally {
        setApprovalStatusRefreshGrid(false);
      }
    }, []);

    // Function to update popup position
    const updatePopupPosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPopupPosition({
          top: rect.bottom + window.scrollY - 5, // Calculate the position relative to the viewport and page
          left: rect.left + window.scrollX - 575, // Adjust the left position to move it to the left side
        });
      }
    };

    useEffect(() => {
      if (showPopup) {
        loadApprovalPageData();
        updatePopupPosition();
      }

      const handleClickOutside = (event: any) => {
        if (
          popupRef.current &&
          !popupRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setShowPopup(false);
        }
      };

      if (showPopup) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [showPopup]);

    const columnDefs = [
      {
        headerName: "Approver Name",
        field: "UserFullName",
        flex: 1,
        minWidth: 130,
        filter: false,
      },
      {
        headerName: "Approver Role",
        field: "UserRole",
        flex: 1,
        minWidth: 130,
        filter: false,
      },
      {
        headerName: "Approval Status",
        field: "ApprovalStatus",
        flex: 1,
        minWidth: 130,
        filter: false,
      },
      {
        headerName: "Reason",
        field: "Message",
        cellRenderer: MessageCellRenderer,
        flex: 2,
        minWidth: 200,
        filter: false,
      },
      {
        headerName: "Updated Date",
        field: "UpdatedDate",
        cellRenderer: TimeRenderer,
        flex: 1,
        minWidth: 160,
        filter: false,
      },
    ];

    const handleClick = () => setShowPopup((prev) => !prev);
    const [showTooltip, setShowTooltip] = useState(false);
    const target = useRef(null);

    const formatMessage = (message: any) =>
      message.split("\n").map((line: any, index: any) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));

    const isSupported = props.data["AutoApproval"] === "Y";
    const hasCOIConflict = props.data["COIApprovalStatus"] === "CONFLICT";
    const message = !isSupported
      ? "Auto-approval not supported"
      : hasCOIConflict
        ? "Unresolved COI conflict"
        : "Auto-approval supported";
    return (
      props.data["ApprovalStatus"] !== ""
        ?
        <>
          <div className={styles.approvalStatusWrapper}>
            <span style={{ flexGrow: 1, textAlign: "left" }}>
              {props.data["ApprovalStatus"]}
            </span>
            <Button
              className={styles.autoApprovalIconWrapper}
              ref={target}
              onClick={() => setShowTooltip(true)}
            >
              {isSupported ? (
                hasCOIConflict ? (
                  <Icon.ShieldExclamation className={styles.coiConflictIcon} />
                ) : (
                  <Icon.CheckCircle className={styles.supportedIcon} />
                )
              ) : (
                null
              )}
            </Button>

            <Overlay
              target={target.current}
              show={showTooltip}
              placement="right"
              rootClose
              onHide={() => setShowTooltip(false)}
            >
              {(props: any) => (
                <Tooltip {...props} id="button-tooltip">
                  {formatMessage(message || "")}
                </Tooltip>
              )}
            </Overlay>
            {props.data["ApprovalStatus"] ? (
              <div
                onClick={handleClick}
                ref={buttonRef}
              >
                <Button className={styles.patchQuestionIcon} >
                  <Icon.PatchQuestion />
                </Button>
              </div>
            )
              :
              null
            }
          </div>

          {showPopup && createPortal(
            <div
              ref={popupRef}
              style={{
                position: "absolute",
                top: popupPosition.top,
                left: popupPosition.left,
                zIndex: 1000,
                width: "600px",
                border: "1px solid #ccc",
                backgroundColor: "white",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              }}
              className="approvalStatusPopup"
            >
              <div style={{ height: "200px", width: "100%" }}>
                {!approvalStatusRefreshGrid
                  ?
                  <>
                    {/* <AgGridReact
                      rowData={approversData}
                      columnDefs={columnDefs}
                      defaultColDef={{ flex: 1 }}
                      suppressContextMenu={true}
                      suppressCellFocus={true}
                      onFirstDataRendered={onFirstDataRendered}
                      noRowsOverlayComponent={noRowsOverlayComponent}
                      noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                    /> */}
                    <CygnusGrid
                      columnDefs={columnDefs}
                      fetchData={async () => {
                        // Since this is not paginated, just return everything
                        const groupedApproversData = groupCOIApprovers(approversData);
                        return { records: groupedApproversData, totalCount: groupedApproversData.length };
                      }}
                      dataIdentifier={"ApproverId"}
                      rowSelection={"single"}
                    />
                  </>
                  :
                  <div
                    className="icon-cell-container"
                  >
                    <div className="spinner flex-column-center-center" style={{ fontSize: 14, fontWeight: 500 }}>
                      <Bars
                        height="80"
                        width="80"
                        color="var(--theme1_primary)"
                        ariaLabel="bars-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                        visible={true}
                      />

                      Loading your data
                    </div>
                  </div>
                }
              </div>
            </div>,
            document.body
          )}
        </>
        :
        null
    );
};

export default ApprovalStatusRenderer;
